import { createStore } from '../core/state/store';
import { fetchBattleHistory, fetchMyColonyId, type BattleHistoryEntry, type FormationId } from '../screens/battle/battle-api';

/**
 * The player's marches that are on the road right now — the ones they sent out, and enemy marches that have been
 * revealed as coming for them — as a small store the HUD chips (colony view, world map) read from, the same way the
 * running boosts are. Polled lazily from the battle history; `marches:refresh` (fired after sending or recalling an
 * army) re-reads immediately.
 */

export interface MarchView {
  id: string;
  /** Outgoing = your army marching on someone; incoming = a revealed enemy march on your colony. */
  kind: 'outgoing' | 'incoming';
  /** The other colony: where you are going, or who is coming. */
  otherName: string;
  /** Your colony's name, for incoming marches (what is being attacked). */
  ownName: string;
  startedAt: string;
  arrivalAt: string;
  formation?: FormationId;
  distanceTiles: number;
}

export const marchStore = createStore<MarchView[]>([]);

const REFRESH_MS = 30_000;

/** Pure: which history rows are marches still on the road. Newest arrival last, so the soonest sits on top. */
export function marchesFromHistory(rows: BattleHistoryEntry[], myColonyId: string | null, now: number = Date.now()): MarchView[] {
  const out: MarchView[] = [];
  for (const r of rows) {
    if (r.status !== 'PENDING') continue;
    if (!(new Date(r.arrivalAt).getTime() > now)) continue;
    const incoming = !!myColonyId && r.defenderId === myColonyId && r.attackerId !== myColonyId;
    out.push({
      id: r.id,
      kind: incoming ? 'incoming' : 'outgoing',
      otherName: incoming ? r.attackerColonyName : r.defenderColonyName,
      ownName: incoming ? r.defenderColonyName : r.attackerColonyName,
      startedAt: r.startedAt,
      arrivalAt: r.arrivalAt,
      formation: incoming ? undefined : r.formation,
      distanceTiles: r.distanceTiles,
    });
  }
  return out.sort((a, b) => new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime());
}

/** 0-100: how far along a march is, from the timestamps the server gave. */
export function marchProgress(m: Pick<MarchView, 'startedAt' | 'arrivalAt'>, now: number = Date.now()): number {
  const start = new Date(m.startedAt).getTime();
  const end = new Date(m.arrivalAt).getTime();
  if (!(end > start)) return 100;
  return Math.max(0, Math.min(100, ((now - start) / (end - start)) * 100));
}

/** "4:07" under an hour, "1:02:07" above — a march timer ticks by the second. */
export function formatMarchClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

let myColonyId: string | null = null;

export async function refreshMarches(): Promise<void> {
  try {
    if (!myColonyId) myColonyId = await fetchMyColonyId();
    marchStore.set(marchesFromHistory(await fetchBattleHistory(), myColonyId));
  } catch {
    // Keep what we had — a failed poll must not blank the HUD.
  }
}

/** Starts polling and listening for `marches:refresh`. Returns the teardown. */
export function startMarchPolling(): () => void {
  void refreshMarches();
  const timer = window.setInterval(() => void refreshMarches(), REFRESH_MS);
  const listener = () => void refreshMarches();
  window.addEventListener('marches:refresh', listener);
  return () => {
    window.clearInterval(timer);
    window.removeEventListener('marches:refresh', listener);
    marchStore.set([]);
    myColonyId = null;
  };
}
